import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { TabletButton } from '@campfire/tablet-button';
import { Box } from '@material-ui/core';
import { ChevronRightRounded } from '@material-ui/icons';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TemplateSnackbar } from '../../../../common/snackbars/TemplateSnackbar';
import { useOnboardingContext } from '../../../../global/onboarding/useOnboardingContext';
import {
  ACTIVITY_ENROLMENT_PROMPT_COPY_GENERAL,
  ACTIVITY_ENROLMENT_PROMPT_COPY_NON_PRIVATE,
} from './activity-enrolment-prompt-copy';
import { ActivityEnrolmentPrompt } from './ActivityEnrolmentPrompt';
import { useActivityApplicationFetch } from './use-activity-application-fetch';
import { EventSessionPickerDialog } from './EventSessionPickerDialog';
import { EventEnrolmentResultDialog } from './EventEnrolmentResultDialog';
import { ActivitiesExploreActivity } from '../__generated__/ActivitiesExploreActivity';

type Props = {
  isVolunteerEnrolled?: boolean;
  volunteerHasAvailability: boolean;
  activityId: string;
  programsLoading: boolean;
  refetch?: () => void;
  activity?: ActivitiesExploreActivity;
};
const ActivitiesExploreNonPrivateActivityApplication = ({
  isVolunteerEnrolled,
  volunteerHasAvailability,
  activityId,
  programsLoading,
  refetch,
  activity,
}: Props) => {
  const { activityApplicationPostRequest, createApplicationLoading, snackBarOptions } = useActivityApplicationFetch(
    refetch
  );
  const { currentOnboardingStep, refresh } = useOnboardingContext();
  const [showSessionPicker, setShowSessionPicker] = useState(false);
  const [showEnrolmentResult, setShowEnrolmentResult] = useState(false);

  const isEventActivity = activity && 'isEvent' in activity && (activity as any).isEvent;
  const sessions = activity?.sessions ?? [];
  const hasMultipleSessions = sessions.length > 1;
  const rosterTemplateCount = (activity as any)?.rosterTemplates?.length ?? 1;
  const hasMultipleDates = rosterTemplateCount > 1;
  const isSingleSessionSingleDate = sessions.length <= 1 && !hasMultipleDates;
  const shouldShowSessionPicker = isEventActivity && hasMultipleSessions;

  const successHandler = () => {
    if (currentOnboardingStep === 1 || currentOnboardingStep === 2) refresh();
  };

  const handleJoinClick = () => {
    if (isEventActivity) {
      activityApplicationPostRequest({ activityId }, 'create', () => {
        successHandler();
        if (shouldShowSessionPicker) {
          setShowSessionPicker(true);
        } else {
          setShowEnrolmentResult(true);
        }
      });
    } else {
      activityApplicationPostRequest({ activityId }, 'create', successHandler);
    }
  };

  const handleSessionConfirm = (selectedSessionIds: string[]) => {
    const activityDate = activity?.startDate;
    if (activityDate) {
      activityApplicationPostRequest(
        {
          activityId,
          activityDates: [{ activityDate, sessionIds: selectedSessionIds }],
        },
        'update',
        () => {
          setShowSessionPicker(false);
          if (refetch) refetch();
        }
      );
    }
  };

  const enrollmentPromptProps = isVolunteerEnrolled
    ? {
        ...(volunteerHasAvailability
          ? ACTIVITY_ENROLMENT_PROMPT_COPY_GENERAL.keepUpToDate
          : ACTIVITY_ENROLMENT_PROMPT_COPY_GENERAL.chooseAvailability),
        action: (
          <Link style={{ color: 'inherit', textDecoration: 'none' }} to={`my-activities`}>
            <TabletButton
              variant='contained'
              size='medium'
              color='primary'
              endIcon={<ChevronRightRounded />}
              onClick={() =>
                activityApplicationPostRequest(
                  {
                    activityId: activityId,
                    activityDates: undefined,
                  },
                  'create',
                  successHandler
                )
              }
            >
              {'View Upcoming Dates'}
            </TabletButton>
          </Link>
        ),
        progress: volunteerHasAvailability ? undefined : 66,
      }
    : {
        ...ACTIVITY_ENROLMENT_PROMPT_COPY_NON_PRIVATE.join,
        action: (
          <TabletButton variant='contained' size='medium' color='primary' onClick={handleJoinClick}>
            {'Join Now'}
          </TabletButton>
        ),
        progress: 0,
      };

  return (
    <>
      {snackBarOptions && (
        <TemplateSnackbar
          variant={snackBarOptions.variant}
          message={snackBarOptions.message}
          open={snackBarOptions.open}
        />
      )}

      <Box position='relative'>
        <CircularProgressOverlay isLoading={createApplicationLoading || programsLoading} />
        <ActivityEnrolmentPrompt {...enrollmentPromptProps} />
      </Box>

      {showEnrolmentResult && activity && (
        <EventEnrolmentResultDialog
          open={showEnrolmentResult}
          onClose={() => setShowEnrolmentResult(false)}
          activityName={activity.name}
          sessionCount={sessions.length}
          dateCount={rosterTemplateCount}
          hasOpenRoster={activity.hasOpenRoster}
          isAutoRostered={isSingleSessionSingleDate && activity.hasOpenRoster}
        />
      )}

      {showSessionPicker && activity && (
        <EventSessionPickerDialog
          open={showSessionPicker}
          onClose={() => {
            setShowSessionPicker(false);
            setShowEnrolmentResult(true);
          }}
          onConfirm={handleSessionConfirm}
          sessions={sessions}
          activityName={activity.name}
          hasOpenRoster={activity.hasOpenRoster}
          isLoading={createApplicationLoading}
        />
      )}
    </>
  );
};

export { ActivitiesExploreNonPrivateActivityApplication };
